/**
 * KPI row trends: raw % / arrow = (this run − 30-day avg) / avg.
 * Color (green/red) = semantically better / worse for that KPI (see KPI_HIGHER_IS_BETTER).
 */

import { KPI_HIGHER_IS_BETTER } from './activityKpiTrendSemantics';
import { formatDerivedKpiDisplay } from './activityKpis';

function formatPctSigned(rawDeltaPct) {
  if (rawDeltaPct === null || !Number.isFinite(rawDeltaPct)) return null;
  if (Math.abs(rawDeltaPct) < 0.05) return '0%';
  const sign = rawDeltaPct > 0 ? '+' : '';
  return `${sign}${rawDeltaPct.toFixed(1)}%`;
}

/** Raw % difference: positive = this run’s KPI value higher than the monthly average. */
function rawDeltaPctVersusBaseline(current, baseline) {
  if (!Number.isFinite(current) || !Number.isFinite(baseline) || baseline === 0) return null;
  return ((current - baseline) / baseline) * 100;
}

function semanticOutcome(key, rawDeltaPct) {
  if (rawDeltaPct === null || !Number.isFinite(rawDeltaPct) || Math.abs(rawDeltaPct) < 0.05) {
    return { semanticGood: null };
  }
  const higher = KPI_HIGHER_IS_BETTER[key];
  const good = higher ? rawDeltaPct > 0 : rawDeltaPct < 0;
  return { semanticGood: good };
}

/**
 * @param {Array<{ key: string, numericValue: number, label?: string }>} kpis — from computeKpis
 * @param {object|null} baseline — API payload with derivedKpiAverages
 * @param {function} t
 */
export function buildDerivedKpiRowTrends(kpis, baseline, t) {
  if (!kpis?.length || !baseline?.derivedKpiAverages || !baseline.sampleCount || baseline.sampleCount < 1) {
    return {};
  }

  const avgs = baseline.derivedKpiAverages;
  const intro = t('activityDetail.kpiTrend.intro', {
    type: baseline.activityType || '—',
    count: baseline.sampleCount,
  });

  const out = {};
  for (const k of kpis) {
    const key = k.key;
    const b = avgs[key];
    const raw = rawDeltaPctVersusBaseline(k.numericValue, b);
    if (raw === null) continue;

    const { semanticGood } = semanticOutcome(key, raw);
    const thisRun = formatDerivedKpiDisplay(key, k.numericValue);
    const monthAvg = formatDerivedKpiDisplay(key, b);
    const rawPctLabel = formatPctSigned(raw);

    const explain = t(`activityDetail.kpiTrend.explain.${key}`);
    const tooltip = t('activityDetail.kpiTrend.cardTooltip', {
      intro,
      label: k.label || key,
      thisRun,
      monthAvg,
      rawPct: rawPctLabel,
      explain,
    });

    out[key] = {
      rawDeltaPct: raw,
      pctLabel: rawPctLabel,
      semanticGood,
      tooltip,
    };
  }
  return out;
}
